import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Tooltip } from './Tooltip';
import { cn } from '@/design-system/utils/cn';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'soft' | 'subtle';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  isLoading?: boolean;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = 'primary',
  size = 'md',
  children,
  isLoading = false,
  className,
  disabled,
  asChild = false,
  title,
  ...props
}, ref) {
  const baseStyles = 'inline-flex h-8 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:h-4 [&_svg]:w-4';

  const subtleStyles = 'bg-slate-200/80 text-primary enabled:hover:bg-slate-200 enabled:active:bg-slate-300 focus-visible:ring-primary/30';
  const variants = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary-hover focus:ring-primary/30',
    secondary: subtleStyles,
    soft: 'bg-primary/10 text-primary hover:bg-primary/15 focus-visible:ring-primary/30',
    subtle: subtleStyles,
    // Preserve older consumers while applying the filled neutral action style.
    outline: subtleStyles,
    ghost: 'bg-transparent text-slate-500 enabled:hover:bg-slate-200/80 enabled:hover:text-primary enabled:active:bg-slate-300 focus-visible:ring-primary/30',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  };

  const sizes = {
    sm: 'px-2.5',
    md: 'px-3',
    lg: 'px-4',
  };

  const Component = asChild ? Slot : 'button';
  const button = (
    <Component
      ref={ref}
      aria-label={props['aria-label'] ?? title}
      className={cn(baseStyles, variants[variant], sizes[size], className)}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Carregando...
        </>
      ) : (
        children
      )}
    </Component>
  );
  return title ? <Tooltip content={title}>{button}</Tooltip> : button;
});
